// Session-end finalize — fold the ENDING session into the archive in the same
// hook run, instead of leaving it for the next session start.
//
// Why this exists: the ingest live-guard (memory-extended, 30s) rightly skips
// any transcript written moments ago, so the session that is ending right now
// was always deferred to the NEXT start/end — "the last session of the day
// never reaches the remote until tomorrow". The host, however, TELLS us which
// session is ending: Claude Code pipes the hook payload (`transcript_path`,
// `reason`, …) into the SessionEnd hook's stdin. With that authoritative
// signal we don't need the 30s heuristic for THIS one file — we wait until it
// stops changing (size+mtime stable for a short window, hard-capped), then run
// the normal archive sync with the live-guard lowered just enough to admit
// files at least as quiet as the ending transcript.
//
// Detach history (two measurements, both real): 2026-06-11 a detached child
// spawned from the STATUSLINE died with its Claude Code host, so this stayed
// bounded in-hook work for a while. 2026-06-28→07-03 the hook-shaped detach
// (the SessionEnd command itself spawns the child and returns at once) proved
// out on the maintainer's Windows instance — five days of completed runs after
// host teardown — so session-end now self-detaches (session-end-detach.ts) and
// THIS module usually runs inside that surviving child. Where a child is still
// killed, the standing next-session net below covers it, same as before.
//
// Safety properties preserved:
// - The next-session catch-up net is untouched: a killed terminal / crash /
//   hook timeout means this never ran, and session-start ingests as before.
// - Self-healing stays: if the host appends transcript lines AFTER the hook
//   (its own bookkeeping), the source mtime advances and the next catch-up
//   re-ingests the complete copy.
// - Admitting "files at least as quiet as the ending transcript" can also
//   ingest another OPEN-but-idle session — which the 30s guard does too (an
//   idle pause > 30s has always made an open session ingestable); both lean
//   on the same re-ingest self-heal, so this widens no failure mode.
//
// Everything here is best-effort: any missing/odd input degrades to "no
// override" and the caller runs the plain sync, exactly as before 0.14.2.

import { statSync } from "node:fs";

/** Minimal structural view of stdin — lets tests pass a PassThrough. */
export interface HookInputStream {
  readonly isTTY?: boolean | undefined;
  setEncoding(encoding: BufferEncoding): unknown;
  on(event: string, listener: (...args: never[]) => void): unknown;
  removeListener(event: string, listener: (...args: never[]) => void): unknown;
  destroy?(error?: Error): unknown;
}

/**
 * Drain the host's hook payload from a stream, bounded in BOTH time and size.
 * Claude Code writes one small JSON object and closes the pipe, so the normal
 * path resolves on `end` within milliseconds; the time bound only guards an
 * odd caller that holds the pipe open, and the byte cap stops a runaway
 * writer — on cap the stream is destroyed, the buffer truncated, and no
 * further chunks are appended (listeners are removed on settle). A TTY stream
 * (someone running `vortex session-end` by hand) resolves "" immediately —
 * never wait on a human.
 */
export function readHookStream(
  stream: HookInputStream,
  opts?: { readonly maxMs?: number; readonly maxBytes?: number },
): Promise<string> {
  if (stream.isTTY) return Promise.resolve("");
  const maxMs = opts?.maxMs ?? 2_000;
  const maxBytes = opts?.maxBytes ?? 1_000_000;
  return new Promise((resolve) => {
    let buf = "";
    let settled = false;
    const onData = (chunk: string | Buffer): void => {
      if (settled) return;
      buf += typeof chunk === "string" ? chunk : chunk.toString("utf8");
      if (buf.length >= maxBytes) {
        buf = buf.slice(0, maxBytes);
        try {
          stream.destroy?.();
        } catch {
          // best effort — we settle either way
        }
        finish();
      }
    };
    const onEnd = (): void => finish();
    const onError = (): void => finish();
    const finish = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      stream.removeListener("data", onData as never);
      stream.removeListener("end", onEnd as never);
      stream.removeListener("error", onError as never);
      resolve(buf);
    };
    const timer = setTimeout(() => {
      try {
        stream.destroy?.();
      } catch {
        // best effort — we settle either way
      }
      finish();
    }, maxMs);
    stream.setEncoding("utf8");
    stream.on("data", onData as never);
    stream.on("end", onEnd as never);
    stream.on("error", onError as never);
  });
}

/** The fields of the host's hook payload that finalize cares about. */
export interface HookStdinPayload {
  /** Absolute path of the ending session's transcript, or null. */
  readonly transcriptPath: string | null;
  /** SessionEnd reason (`clear` / `logout` / `exit` / …), or null. */
  readonly reason: string | null;
}

/**
 * Parse a hook stdin payload (Claude Code writes one JSON object). Tolerant by
 * design: anything that is not a JSON object with a string `transcript_path`
 * yields nulls — the caller then behaves exactly like the pre-finalize hook.
 */
export function parseHookStdin(raw: string): HookStdinPayload {
  const none: HookStdinPayload = { transcriptPath: null, reason: null };
  if (!raw || !raw.trim()) return none;
  try {
    const v: unknown = JSON.parse(raw);
    if (typeof v !== "object" || v === null) return none;
    const o = v as Record<string, unknown>;
    const path = typeof o["transcript_path"] === "string" && o["transcript_path"].trim() ? o["transcript_path"] : null;
    const reason = typeof o["reason"] === "string" && o["reason"].trim() ? o["reason"] : null;
    return { transcriptPath: path, reason };
  } catch {
    return none;
  }
}

export interface StabilityOptions {
  /** Poll interval. */
  readonly pollMs?: number;
  /** How long (size, mtime) must hold still to count as stable. */
  readonly stableForMs?: number;
  /** Hard cap on the whole wait — on expiry the caller gets null and falls back. */
  readonly maxWaitMs?: number;
}

const STABILITY_DEFAULTS = { pollMs: 300, stableForMs: 1_200, maxWaitMs: 12_000 } as const;

/**
 * Wait until `path` stops changing: (size, mtimeMs) unchanged for
 * `stableForMs`, polled every `pollMs`, the whole wait capped at `maxWaitMs`.
 * Resolves to the file's final mtimeMs, or null when the file is absent or
 * never stabilized within the cap (caller falls back to the plain sync).
 *
 * A fixed sleep was rejected here on review: it is a guess, not a proof, and
 * it spends its full duration even when the file settled instantly. This
 * polls, so the typical cost is just `stableForMs` (+ one poll).
 *
 * Already-elapsed quiet time is CREDITED (0.14.3): if the file's mtime says it
 * has been still for `stableForMs` already, that is the same backward-looking
 * guarantee the polling loop would buy by waiting — so the wait returns
 * immediately instead of re-waiting on top of it. Measured 2026-06-11: the
 * end hook always paid the full 1.2 s here even though an exiting session's
 * transcript had typically been quiet for seconds, and that delay pushed the
 * commit/push tail past the host's undocumented kill window at exit. A future
 * mtime (clock skew) earns no credit — the loop then polls exactly as before.
 */
export async function waitForTranscriptStability(
  path: string,
  opts?: StabilityOptions,
): Promise<number | null> {
  const pollMs = opts?.pollMs ?? STABILITY_DEFAULTS.pollMs;
  const stableForMs = opts?.stableForMs ?? STABILITY_DEFAULTS.stableForMs;
  const maxWaitMs = opts?.maxWaitMs ?? STABILITY_DEFAULTS.maxWaitMs;
  const probe = (): { size: number; mtimeMs: number } | null => {
    try {
      const s = statSync(path);
      return s.isFile() ? { size: s.size, mtimeMs: s.mtimeMs } : null;
    } catch {
      return null;
    }
  };
  const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

  const start = Date.now();
  let last = probe();
  if (!last) return null;
  let quietSince = start - Math.max(0, start - last.mtimeMs);
  for (;;) {
    if (Date.now() - quietSince >= stableForMs) return last.mtimeMs;
    const elapsed = Date.now() - start;
    if (elapsed >= maxWaitMs) return null;
    // Never sleep past the cap — maxWaitMs is a HARD bound on the whole wait.
    await sleep(Math.min(pollMs, maxWaitMs - elapsed));
    const cur = probe();
    if (!cur) return null;
    if (cur.size !== last.size || cur.mtimeMs !== last.mtimeMs) {
      last = cur;
      quietSince = Date.now();
    }
  }
}

/**
 * The ingest live-guard override that admits the ending transcript: its age at
 * sync time, floored at 0.5s (never pass a degenerate guard) and capped at the
 * stock 30s (an older transcript needs no override at all). Ingest re-checks
 * `now - mtime >= guard` with a LATER `now`, so the age computed here only
 * grows by then — the target stays admitted.
 *
 * The floor tracks the end-phase stability window (0.14.4: both 0.5s, down
 * from 1s/1.2s). They must move together: with a floor ABOVE the stability
 * window, a transcript that stabilized quickly gets a guard larger than its
 * age and the ingest's own `now - mtime >= guard` re-check can race to a SKIP
 * — the finalize would wait, pass, and then archive nothing. 0.5s is still a
 * meaningful guard (stability just proved that much quiet), and a line the
 * host appends after the wait is covered by the standing mtime self-heal.
 */
export function liveSkipForFinalize(mtimeMs: number, nowMs: number = Date.now()): number {
  const age = Math.floor(nowMs - mtimeMs);
  return Math.max(500, Math.min(age, 30_000));
}
